import {
	ICurrencyTransaction,
	ITransaction,
	SupportedJurisdictions,
	useAddTransaction,
	useCurrentUser,
	useEditTransaction,
	useJurisdictionProperties,
	useTransactionById,
} from 'api'
import { TransactionType } from 'api/transactions/dtos'
import { useHideModal } from 'components/Modals'
import { useFormikContext } from 'formik'
import { useLocalization } from 'localization'
import { useState } from 'react'
import { useTheme } from 'styled-components'
import { createValidationSchema } from 'utils/validation'
import { isTransactionRequiredFieldByDirection } from './helpers'

export interface ITransactionFormValues extends Partial<ITransaction> {
	inAmount?: ICurrencyTransaction['amount']
	inCurrency?: ICurrencyTransaction['currency']
	inPerCoin?: ICurrencyTransaction['perCoin']
	inValuation?: ICurrencyTransaction['valuation']
	outAmount?: ICurrencyTransaction['amount']
	outCurrency?: ICurrencyTransaction['currency']
	outPerCoin?: ICurrencyTransaction['perCoin']
	outValuation?: ICurrencyTransaction['valuation']
	calculatedInValuation?: ICurrencyTransaction['valuation']
	calculatedOutValuation?: ICurrencyTransaction['valuation']
	calculatedFeeValuation?: ICurrencyTransaction['valuation']
}

interface IDynamicFields {
	outgoing?: {
		title?: string
		placeholder: string
		noValuation?: boolean
		amountNotRequired?: boolean
		currencyNotRequired?: boolean
		dynamicVisibility?: boolean
		internalWallet?: boolean
	}
	incoming?: {
		placeholder: string
		fork?: boolean
		noValuation?: boolean
		amountNotRequired?: boolean
		currencyNotRequired?: boolean
		dynamicVisibility?: boolean
		internalWallet?: boolean
	}
	blockchain?: {
		outInternalWallet?: boolean
		inInternalWallet?: boolean
	}
	noFee?: boolean
	showAcquisitionDate?: boolean
	showAdditionalCosts?: boolean
	showCapitalLoss?: boolean
	wallet?: boolean
}

export interface IInnerCalculated {
	in?: ICurrencyTransaction['valuation']
	out?: ICurrencyTransaction['valuation']
	fee?: ICurrencyTransaction['valuation']
}

export const useTransactionModal = (id?: ITransaction['id']) => {
	const { t } = useLocalization()
	const hideModal = useHideModal()
	const theme = useTheme()
	const transactionOptions = useTransactionOptions()
	const { data: transaction, isLoading: isTransactionLoading, isFetched: isTransactionFetched } = useTransactionById(id)
	const { data: user } = useCurrentUser()
	const { getTransactionProperties } = useJurisdictionProperties(user?.jurisdiction)
	const { mutateAsync: editTransaction } = useEditTransaction()
	const { mutateAsync: addTransaction } = useAddTransaction()
	const [innerCalculated, setInnerCalculated] = useState<IInnerCalculated>({})

	const initialValues: ITransactionFormValues = transaction
		? {
				...transaction,
				inAmount: transaction.in?.amount,
				inCurrency: transaction.in?.currency,
				inPerCoin: transaction.in?.perCoin,
				inValuation: transaction.in?.valuation,
				outAmount: transaction.out?.amount,
				outCurrency: transaction.out?.currency,
				outPerCoin: transaction.out?.perCoin,
				outValuation: transaction.out?.valuation,
				calculatedFeeValuation: transaction.fee?.valuation,
				calculatedInValuation: transaction.in?.valuation,
				calculatedOutValuation: transaction.out?.valuation,
		  }
		: {}

	const schema = createValidationSchema(yup => {
		return yup.object({
			type: yup.string().required(),
			date: yup.date().required(),
			acquisitionDate: yup.date().nullable().optional(),
			additionalCosts: yup.number().nullable().optional(),
			outAmount: yup
				.number()
				.nullable()
				.when('type', {
					is: (value: TransactionType) =>
						isTransactionRequiredFieldByDirection(value, 'outgoing') &&
						value !== TransactionType.CRYPTO_LIQUIDITY_POOL_WITHDRAWAL &&
						value !== TransactionType.CRYPTO_LENDING_REPAYMENT &&
						value !== TransactionType.CURRENCY_MARGIN_LOSS &&
						value !== TransactionType.CRYPTO_LOST_OR_STOLEN,
					then: yup.number().required(),
				})
				.test('is required because of isMarginLossInValue', t('validation.custom.dynamicRequired'), function (value) {
					const disallowedTypes = [TransactionType.CURRENCY_MARGIN_LOSS]
					const checked = this.parent.isMarginLossInValue
					const isValueEmpty = !value && value !== 0

					if (!disallowedTypes.includes(this.parent.type)) return true

					if (this.parent.type === TransactionType.CRYPTO_LOST_OR_STOLEN) {
						return this.parent.capitalLoss ? true : !isValueEmpty
					}

					return !checked && isValueEmpty ? false : true
				}),
			outPerCoin: yup.number().nullable().optional(),
			outCurrency: yup
				.string()
				.nullable()
				.when('type', {
					is: (value: TransactionType) =>
						isTransactionRequiredFieldByDirection(value, 'outgoing') &&
						value !== TransactionType.CRYPTO_LIQUIDITY_POOL_WITHDRAWAL &&
						value !== TransactionType.CRYPTO_LENDING_REPAYMENT &&
						value !== TransactionType.CURRENCY_MARGIN_LOSS,
					then: yup.string().required(),
				})
				.test('is required because of isMarginLossInValue', t('validation.custom.dynamicRequired'), function (value) {
					const disallowedTypes = [TransactionType.CURRENCY_MARGIN_LOSS]
					const checked = this.parent.isMarginLossInValue
					const isValueEmpty = !value
					return disallowedTypes.includes(this.parent.type) ? (!checked && isValueEmpty ? false : true) : true
				}),
			outValuation: yup.number().nullable().optional(),
			marginLossValue: yup
				.number()
				.nullable()
				.optional()
				.when('isMarginLossInValue', {
					is: (value: boolean) => value === true,
					then: yup.number().required(),
				}),
			inAmount: yup
				.number()
				.nullable()
				.when('type', {
					is: (value: TransactionType) =>
						isTransactionRequiredFieldByDirection(value, 'incoming') &&
						value !== TransactionType.CRYPTO_LIQUIDITY_POOL_DEPOSIT &&
						value !== TransactionType.CRYPTO_LENDING &&
						value !== TransactionType.CURRENCY_MARGIN_PROFIT,
					then: yup.number().required(),
				})
				.test('is required because of isMarginProfitInValue', t('validation.custom.dynamicRequired'), function (value) {
					const disallowedTypes = [TransactionType.CURRENCY_MARGIN_PROFIT]
					const checked = this.parent.isMarginProfitInValue
					const isValueEmpty = !value && value !== 0
					return disallowedTypes.includes(this.parent.type) ? (!checked && isValueEmpty ? false : true) : true
				}),
			inCurrency: yup
				.string()
				.nullable()
				.when('type', {
					is: (value: TransactionType) =>
						isTransactionRequiredFieldByDirection(value, 'incoming') &&
						value !== TransactionType.CRYPTO_LIQUIDITY_POOL_DEPOSIT &&
						value !== TransactionType.CRYPTO_LENDING &&
						value !== TransactionType.CURRENCY_MARGIN_PROFIT,
					then: yup.string().required(),
				})
				.test('is required because of isMarginProfitInValue', t('validation.custom.dynamicRequired'), function (value) {
					const disallowedTypes = [TransactionType.CURRENCY_MARGIN_PROFIT]
					const checked = this.parent.isMarginProfitInValue
					const isValueEmpty = !value
					return disallowedTypes.includes(this.parent.type) ? (!checked && isValueEmpty ? false : true) : true
				}),
			inPerCoin: yup.number().nullable().optional(),
			inValuation: yup.number().nullable().optional(),
			marginProfitValue: yup
				.number()
				.nullable()
				.optional()
				.when('isMarginProfitInValue', {
					is: (value: boolean) => value === true,
					then: yup.number().required(),
				}),
			capitalLoss: yup
				.number()
				.nullable()
				.test('is required when lost or stolen', t('validation.custom.dynamicRequired'), function (value) {
					if (this.parent.type !== TransactionType.CRYPTO_LOST_OR_STOLEN) return true

					return this.parent.outAmount ? true : !!value
				}),
			forkBaseCurrency: yup
				.string()
				.nullable()
				.when('type', {
					is: (value: TransactionType) =>
						value === TransactionType.CRYPTO_FORK && user?.jurisdiction === SupportedJurisdictions.UK,
					then: yup.string().required(),
				}),
		})
	})

	const onSubmit = async (values: ITransactionFormValues) => {
		if (values.id) {
			await editTransaction({
				...values,
				...(values.fee
					? {
							fee: {
								...values.fee,
								valuation:
									(innerCalculated.fee !== undefined &&
										(innerCalculated.fee !== values.calculatedFeeValuation || !!transaction?.fee?.valuation)) ||
									(!innerCalculated.fee && values.calculatedFeeValuation)
										? values.calculatedFeeValuation
										: values.fee.valuation,
							},
					  }
					: undefined),
				in: {
					...values.in,
					amount: values.inAmount,
					currency: values.inCurrency,
					perCoin: values.inPerCoin,
					valuation:
						(innerCalculated.in !== undefined &&
							(innerCalculated.in !== values.calculatedInValuation || !!transaction?.in?.valuation)) ||
						(!innerCalculated.in && values.calculatedInValuation)
							? values.calculatedInValuation
							: values.inValuation,
				},
				out: {
					...values.out,
					amount: values.outAmount,
					currency: values.outCurrency,
					perCoin: values.outPerCoin,
					valuation:
						(innerCalculated.out !== undefined &&
							(innerCalculated.out !== values.calculatedOutValuation || !!transaction?.out?.valuation)) ||
						(!innerCalculated.out && values.calculatedOutValuation)
							? values.calculatedOutValuation
							: values.outValuation,
				},
			} as ITransaction)
		} else {
			await addTransaction({
				...values,
				...(values.fee
					? {
							fee: {
								...values.fee,
								valuation:
									(innerCalculated.fee !== undefined && innerCalculated.fee !== values.calculatedFeeValuation) ||
									(!innerCalculated.fee && values.calculatedFeeValuation)
										? values.calculatedFeeValuation
										: null,
							},
					  }
					: undefined),
				in: {
					...values.in,
					amount: values.inAmount,
					currency: values.inCurrency,
					perCoin: values.inPerCoin,
					valuation:
						(innerCalculated.in !== undefined && innerCalculated.in !== values.calculatedInValuation) ||
						(!innerCalculated.in && values.calculatedInValuation)
							? values.calculatedInValuation
							: null,
				},
				out: {
					...values.out,
					amount: values.outAmount,
					currency: values.outCurrency,
					perCoin: values.outPerCoin,
					valuation:
						(innerCalculated.out !== undefined && innerCalculated.out !== values.calculatedOutValuation) ||
						(!innerCalculated.out && values.calculatedOutValuation)
							? values.calculatedOutValuation
							: null,
				},
			} as ITransaction)
		}

		hideModal()
	}

	return {
		theme,
		initialValues,
		schema,
		transactionOptions,
		isTransactionLoading,
		isTransactionFetched,
		innerCalculated,
		getTransactionProperties,
		t,
		hideModal,
		onSubmit,
		setInnerCalculated,
	}
}

export const useDynamicTransactionFields = (jurisdiction?: SupportedJurisdictions): IDynamicFields => {
	const { t } = useLocalization()
	const { values } = useFormikContext<ITransactionFormValues>()
	const { data: user } = useCurrentUser()

	switch (values.type) {
		case TransactionType.CRYPTO_FORK:
			return {
				incoming: {
					placeholder: t('placeholders.crypto'),
					fork: user?.jurisdiction === SupportedJurisdictions.UK,
				},
				blockchain: {
					inInternalWallet: true,
				},
			}
		case TransactionType.CURRENCY_MARGIN_PROFIT:
			return {
				incoming: {
					placeholder: t('placeholders.crypto'),
					dynamicVisibility: true,
				},
				blockchain: {
					inInternalWallet: true,
				},
			}
		case TransactionType.CURRENCY_MARGIN_LOSS:
			return {
				outgoing: {
					placeholder: t('placeholders.crypto'),
					dynamicVisibility: true,
				},
				blockchain: {
					outInternalWallet: true,
				},
			}
		case TransactionType.CRYPTO_BORROWING:
		case TransactionType.CRYPTO_STAKING_REWARD:
		case TransactionType.CRYPTO_INCOME_REWARD:
		case TransactionType.CRYPTO_INTEREST_INCOME:
		case TransactionType.CRYPTO_ICO_IDO:
		case TransactionType.CRYPTO_RECEIVE_PAYMENT:
		case TransactionType.CRYPTO_MINING:
		case TransactionType.CRYPTO_AIRDROP:
		case TransactionType.CRYPTO_SPAM:
		case TransactionType.CRYPTO_RECEIVE_NON_TAXABLE:
			return {
				incoming: {
					placeholder: t('placeholders.crypto'),
				},
				blockchain: {
					inInternalWallet: true,
				},
			}
		case TransactionType.CRYPTO_RECEIVE_GIFT_OR_DONATION:
			return {
				incoming: {
					placeholder: t('placeholders.crypto'),
				},
				blockchain: {
					inInternalWallet: true,
				},
				showAcquisitionDate: jurisdiction === SupportedJurisdictions.USA,
			}
		case TransactionType.CRYPTO_TRANSFER:
			return {
				outgoing: {
					title: t('terms.moving'),
					placeholder: t('placeholders.crypto'),
					noValuation: true,
				},
				blockchain: {
					inInternalWallet: true,
					outInternalWallet: true,
				},
			}
		case TransactionType.CRYPTO_OTHER_FEES:
			return {
				outgoing: {
					placeholder: t('placeholders.cryptoOrFiat'),
				},
				blockchain: {
					outInternalWallet: true,
				},
				noFee: true,
			}
		case TransactionType.CRYPTO_DEBT_REPAYMENT:
		case TransactionType.CRYPTO_EXPENSE:
		case TransactionType.CRYPTO_SEND_NON_TAXABLE:
		case TransactionType.CRYPTO_SEND_GIFT_OR_DONATION:
		case TransactionType.CRYPTO_SEND_PAYMENT:
		case TransactionType.CRYPTO_SEND_TO_UNKNOWN:
		case TransactionType.CRYPTO_LIQUIDATION:
			return {
				outgoing: {
					placeholder: t('placeholders.crypto'),
				},
				blockchain: {
					outInternalWallet: true,
				},
			}
		case TransactionType.CRYPTO_LOST_OR_STOLEN:
			return {
				outgoing: {
					placeholder: t('placeholders.crypto'),
					amountNotRequired:
						(jurisdiction === SupportedJurisdictions.UK || jurisdiction === SupportedJurisdictions.SA) &&
						!!values.capitalLoss,
				},
				blockchain: {
					outInternalWallet: true,
				},
				showCapitalLoss: jurisdiction === SupportedJurisdictions.UK || jurisdiction === SupportedJurisdictions.SA,
			}
		case TransactionType.FIAT_DEPOSIT:
			return {
				incoming: {
					placeholder: t('placeholders.fiat'),
					noValuation: true,
					internalWallet: true,
				},
				noFee: true,
			}
		case TransactionType.FIAT_WITHDRAWAL:
			return {
				outgoing: {
					placeholder: t('placeholders.fiat'),
					noValuation: true,
					internalWallet: true,
				},
				noFee: true,
			}
		case TransactionType.CRYPTO_LIQUIDITY_POOL_WITHDRAWAL:
			return {
				outgoing: {
					placeholder: t('placeholders.lp'),
					amountNotRequired: true,
					currencyNotRequired: true,
				},
				incoming: {
					placeholder: t('placeholders.crypto'),
				},
				wallet: true,
			}
		case TransactionType.CRYPTO_LIQUIDITY_POOL_DEPOSIT:
			return {
				outgoing: {
					placeholder: t('placeholders.crypto'),
				},
				incoming: {
					placeholder: t('placeholders.lp'),
					currencyNotRequired: true,
					amountNotRequired: true,
				},
				wallet: true,
			}
		case TransactionType.CRYPTO_NON_TAXABLE_SWAP:
			return {
				outgoing: {
					placeholder: t('placeholders.crypto'),
				},
				incoming: {
					placeholder: t('placeholders.cryptoWest'),
				},
				wallet: true,
			}
		case TransactionType.CRYPTO_MINTED_SELL:
			return {
				outgoing: {
					placeholder: t('placeholders.nft'),
				},
				incoming: {
					placeholder: t('placeholders.cryptoOrFiat'),
				},
				wallet: true,
				showAdditionalCosts: true,
			}
		case TransactionType.CRYPTO_LENDING_REPAYMENT:
			return {
				outgoing: {
					placeholder: t('placeholders.symbolic'),
					amountNotRequired: true,
					currencyNotRequired: true,
				},
				incoming: {
					placeholder: t('placeholders.crypto'),
				},
				wallet: true,
			}
		case TransactionType.CRYPTO_LENDING:
			return {
				outgoing: {
					placeholder: t('placeholders.crypto'),
				},
				incoming: {
					placeholder: t('placeholders.symbolic'),
					currencyNotRequired: true,
					amountNotRequired: true,
				},
				wallet: true,
			}
		case TransactionType.FIAT_TRADE:
			return {
				outgoing: {
					placeholder: t('placeholders.fiat'),
				},
				incoming: {
					placeholder: t('placeholders.fiat'),
				},
				wallet: true,
			}
		case TransactionType.CRYPTO_TRADE:
			return {
				outgoing: {
					placeholder: t('placeholders.crypto'),
				},
				incoming: {
					placeholder: t('placeholders.crypto'),
				},
				wallet: true,
			}
		case TransactionType.CRYPTO_SELL_FOR_FIAT:
			return {
				outgoing: {
					placeholder: t('placeholders.crypto'),
				},
				incoming: {
					placeholder: t('placeholders.fiat'),
				},
				wallet: true,
			}
		case TransactionType.CRYPTO_PURCHASE_FOR_FIAT:
			return {
				outgoing: {
					placeholder: t('placeholders.fiat'),
				},
				incoming: {
					placeholder: t('placeholders.crypto'),
				},
				wallet: true,
			}
		default:
			return {}
	}
}

export const useTransactionOptions = () => {
	const { t } = useLocalization()

	return [
		{
			groupName: t('transactions.groups.CURRENCY_EXCHANGE'),
			types: [
				{
					value: TransactionType.CRYPTO_SELL_FOR_FIAT,
					label: t('transactions.types.Sell'),
				},
				{
					value: TransactionType.CRYPTO_PURCHASE_FOR_FIAT,
					label: t('transactions.types.Buy'),
				},
				{
					value: TransactionType.CRYPTO_TRADE,
					label: t('transactions.types.Trade'),
				},
				{
					value: TransactionType.FIAT_TRADE,
					label: t('transactions.types.Trade Fiat'),
				},
				{
					value: TransactionType.CRYPTO_LENDING,
					label: t('transactions.types.Lending'),
				},
				{
					value: TransactionType.CRYPTO_LENDING_REPAYMENT,
					label: t('transactions.types.Repayment (Lending)'),
				},
				{
					value: TransactionType.CRYPTO_MINTED_SELL,
					label: t('transactions.types.Minted NFT Sell'),
				},
				{
					value: TransactionType.CRYPTO_LIQUIDITY_POOL_DEPOSIT,
					label: t('transactions.types.Liquidity Pool Deposit'),
				},
				{
					value: TransactionType.CRYPTO_LIQUIDITY_POOL_WITHDRAWAL,
					label: t('transactions.types.Liquidity Pool Withdrawal'),
				},
				{
					value: TransactionType.CRYPTO_NON_TAXABLE_SWAP,
					label: t('transactions.types.Non Taxable Swap'),
				},
			],
		},
		{
			groupName: t('transactions.groups.FIAT_MOVEMENT'),
			types: [
				{
					value: TransactionType.FIAT_WITHDRAWAL,
					label: t('transactions.types.Withdraw Fiat'),
				},
				{
					value: TransactionType.FIAT_DEPOSIT,
					label: t('transactions.types.Deposit Fiat'),
				},
			],
		},
		{
			groupName: t('transactions.groups.CRYPTO_OUTFLOW'),
			types: [
				{
					value: TransactionType.CRYPTO_DEBT_REPAYMENT,
					label: t('transactions.types.Debt Repayment'),
				},
				{
					value: TransactionType.CRYPTO_EXPENSE,
					label: t('transactions.types.Expense'),
				},
				{
					value: TransactionType.CRYPTO_OTHER_FEES,
					label: t('transactions.types.Other Fees'),
				},
				{
					value: TransactionType.CRYPTO_LOST_OR_STOLEN,
					label: t('transactions.types.Lost Or Stolen'),
				},
				{
					value: TransactionType.CRYPTO_SEND_GIFT_OR_DONATION,
					label: t('transactions.types.Send Gift Or Donation'),
				},
				{
					value: TransactionType.CRYPTO_SEND_PAYMENT,
					label: t('transactions.types.Send Payment'),
				},
				{
					value: TransactionType.CRYPTO_SEND_TO_UNKNOWN,
					label: t('transactions.types.Send To Unknown'),
				},
				{
					value: TransactionType.CRYPTO_SEND_NON_TAXABLE,
					label: t('transactions.types.Send (Non Taxable)'),
				},
				{
					value: TransactionType.CRYPTO_LIQUIDATION,
					label: t('transactions.types.Liquidation'),
				},
			],
		},
		{
			groupName: t('transactions.groups.CRYPTO_MOVEMENT'),
			types: [
				{
					value: TransactionType.CRYPTO_TRANSFER,
					label: t('transactions.types.Transfer'),
				},
			],
		},
		{
			groupName: t('transactions.groups.CRYPTO_INFLOW'),
			types: [
				{
					value: TransactionType.CRYPTO_INCOME_REWARD,
					label: t('transactions.types.Income / Reward'),
				},
				{
					value: TransactionType.CRYPTO_INTEREST_INCOME,
					label: t('transactions.types.Interest Income'),
				},
				{
					value: TransactionType.CRYPTO_STAKING_REWARD,
					label: t('transactions.types.Staking Reward'),
				},
				{
					value: TransactionType.CRYPTO_AIRDROP,
					label: t('transactions.types.Airdrop'),
				},
				{
					value: TransactionType.CRYPTO_FORK,
					label: t('transactions.types.Fork'),
				},
				{
					value: TransactionType.CRYPTO_MINING,
					label: t('transactions.types.Mining'),
				},
				{
					value: TransactionType.CRYPTO_RECEIVE_NON_TAXABLE,
					label: t('transactions.types.Receive (Non Taxable)'),
				},
				{
					value: TransactionType.CRYPTO_RECEIVE_PAYMENT,
					label: t('transactions.types.Receive Payment'),
				},
				{
					value: TransactionType.CRYPTO_RECEIVE_GIFT_OR_DONATION,
					label: t('transactions.types.Receive Gift Or Donation'),
				},
				{
					value: TransactionType.CRYPTO_BORROWING,
					label: t('transactions.types.Borrowing'),
				},
				{
					value: TransactionType.CRYPTO_ICO_IDO,
					label: t('transactions.types.ICO / IDO'),
				},
				{
					value: TransactionType.CRYPTO_SPAM,
					label: t('transactions.types.Spam'),
				},
			],
		},
		{
			groupName: t('transactions.groups.CRYPTO_OR_FIAT_MOVEMENT'),
			types: [
				{
					value: TransactionType.CURRENCY_MARGIN_PROFIT,
					label: t('transactions.types.Margin Profit'),
				},
				{
					value: TransactionType.CURRENCY_MARGIN_LOSS,
					label: t('transactions.types.Margin Loss'),
				},
			],
		},
	]
}
